/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Card, Chip, CircularProgress, IconButton, TextField, InputAdornment,
  Button, Typography, Table, TableBody, TableCell, TableRow, Alert, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaymentIcon from '@mui/icons-material/Payment';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MDBox from 'components/MDBox';
import MDDatePicker from 'components/MDDatePicker';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';
import printReturnNote from './printReturnNote';

const toISO = (d) => d.toISOString().split('T')[0];
const defaultTo = toISO(new Date());
const defaultFrom = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return toISO(d); })();

const TEAL = '#00798E';
const TEAL_DARK = '#005f70';
const TEAL_LIGHT = '#e0f4f7';

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const REFUND_MODE_STYLE = {
  CASH:        { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7', dot: '#43a047' },
  CREDIT_NOTE: { bg: '#ede7f6', color: '#4527a0', border: '#b39ddb', dot: '#7e57c2' },
  ONLINE:      { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9', dot: '#1e88e5' },
};
const REFUND_MODE_LABEL = { CASH: 'Cash', CREDIT_NOTE: 'Credit Note', ONLINE: 'Online' };
const REFUND_FILTERS = [
  { key: '', label: 'All' },
  { key: 'CASH', label: 'Cash' },
  { key: 'CREDIT_NOTE', label: 'Credit Note' },
  { key: 'ONLINE', label: 'Online' },
];

// ── Stat Card ──────────────────────────────────────────────────

function StatCard({ icon, label, value, accent, sub }) {
  return (
    <Box sx={{
      flex: '1 1 160px', minWidth: 140, borderRadius: 3,
      background: '#fff',
      border: `1.5px solid ${accent}33`,
      boxShadow: `0 2px 12px ${accent}18`,
      px: 2.5, py: 2,
      display: 'flex', alignItems: 'center', gap: 1.8,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: `0 6px 24px ${accent}28` },
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2.5,
        background: `linear-gradient(135deg, ${accent}22, ${accent}44)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {React.cloneElement(icon, { sx: { color: accent, fontSize: 22 } })}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.2 }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 800, color: '#1a202c', lineHeight: 1.4, fontFamily: 'monospace' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '0.65rem', color: '#bdbdbd', mt: 0.2 }}>{sub}</Typography>}
      </Box>
    </Box>
  );
}

// ── Refund Mode Badge ────────────────────────────────────────────

function ModeBadge({ mode }) {
  const s = REFUND_MODE_STYLE[mode] || { bg: '#f5f5f5', color: '#757575', border: '#e0e0e0', dot: '#bdbdbd' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.6,
      px: 1.2, py: 0.35, borderRadius: '20px',
      background: s.bg, border: `1px solid ${s.border}`,
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, lineHeight: 1 }}>{REFUND_MODE_LABEL[mode] || mode}</Typography>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────

export default function ReturnsPage() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState((urlParams && parseInt(urlParams.get('page'), 10)) || 1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [fromDate, setFromDate] = useState(urlParams?.get('from_date') || defaultFrom);
  const [toDate, setToDate] = useState(urlParams?.get('to_date') || defaultTo);
  const [refundMethod, setRefundMethod] = useState(urlParams?.get('refund_method') || '');
  const [error, setError] = useState('');
  const searchTimer = useRef(null);
  const PER_PAGE = 20;

  const kb = useListKeyboard({
    rowCount: rows.length,
    onOpen: (i) => { const b = rows[i]?.original_bill; if (b?.id) navigate(`/pharmacy/billing/${b.id}`); },
  });

  const load = useCallback(async (q, fd, td, rm, pg) => {
    setLoading(true); setError('');
    try {
      writeParams({ search: q, from_date: fd, to_date: td, refund_method: rm, page: pg });
      const params = { page: pg, per_page: PER_PAGE };
      if (q) params.search = q;
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      if (rm) params.refund_method = rm;
      const res = await api.pharmacy.listAllReturns(params);
      setRows(res.rows || []);
      setTotal(res.count || 0);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load returns');
    } finally { setLoading(false); }
  }, [writeParams]);

  useEffect(() => { load(search, fromDate, toDate, refundMethod, page); }, [page]); // eslint-disable-line

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(val, fromDate, toDate, refundMethod, 1); }, 400);
  };

  const handleDateFilter = (fd, td) => {
    setFromDate(fd); setToDate(td);
    setPage(1); load(search, fd, td, refundMethod, 1);
  };

  const handleModeFilter = (rm) => {
    setRefundMethod(rm);
    setPage(1); load(search, fromDate, toDate, rm, 1);
  };

  const totalPages = Math.ceil(total / PER_PAGE);
  const grandTotal = rows.reduce((s, r) => s + Number(r.return_amount || 0), 0);
  const cashTotal = rows.filter(r => r.refund_method === 'CASH').reduce((s, r) => s + Number(r.return_amount || 0), 0);
  const cnTotal = rows.filter(r => r.refund_method === 'CREDIT_NOTE').reduce((s, r) => s + Number(r.return_amount || 0), 0);
  const onlineTotal = rows.filter(r => r.refund_method === 'ONLINE').reduce((s, r) => s + Number(r.return_amount || 0), 0);

  const COL_HEADERS = ['Return No', 'Date', 'Patient', 'Mobile', 'Bill No', 'Reason', 'Refund Mode', 'Amount', 'Status', 'Action'];

  const handlePrint = (ret) => {
    // ret from the list endpoint doesn't carry item lines / full bill items — fetch the full record for print
    api.pharmacy.getBillReturn(ret.id).then(full => printReturnNote(full, selectedPharmacy))
      .catch(() => setError('Failed to load return details for printing'));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3} px={0}>
        {/* ── Hero Header ── */}
        <Box sx={{
          borderRadius: 4,
          background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #003d4d 100%)`,
          px: { xs: 2.5, md: 4 }, py: 3, mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
          boxShadow: `0 8px 32px ${TEAL}44`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -20, right: 80, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, zIndex: 1 }}>
            <IconButton onClick={() => navigate('/pharmacy/billing')}
              sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { background: 'rgba(255,255,255,0.28)' }, borderRadius: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{
              width: 46, height: 46, borderRadius: 2.5,
              background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AssignmentReturnIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Pharmacy Billing</Typography>
              <Typography sx={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.1 }}>Returns &amp; Refunds</Typography>
            </Box>
            <Box sx={{ ml: 1, px: 1.4, py: 0.4, borderRadius: 10, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>{total} total</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, zIndex: 1 }}>
            <Button
              onClick={() => navigate('/pharmacy/billing/payments')}
              startIcon={<PaymentIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                background: 'rgba(255,255,255,0.15)', color: '#fff !important', fontWeight: 700,
                fontSize: '0.78rem', borderRadius: 2.5, px: 2.5, py: 1,
                border: '1.5px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(6px)',
                '&:hover': { background: 'rgba(255,255,255,0.28)' },
              }}>
              Payments
            </Button>
            <Button
              onClick={() => navigate('/pharmacy/billing/credit-notes')}
              startIcon={<ReceiptLongIcon sx={{ fontSize: '16px !important' }} />}
              sx={{
                background: 'rgba(255,255,255,0.15)', color: '#fff !important', fontWeight: 700,
                fontSize: '0.78rem', borderRadius: 2.5, px: 2.5, py: 1,
                border: '1.5px solid rgba(255,255,255,0.3)',
                backdropFilter: 'blur(6px)',
                '&:hover': { background: 'rgba(255,255,255,0.28)' },
              }}>
              Credit Notes
            </Button>
          </Box>
        </Box>

        {/* ── Stat Cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard icon={<AssignmentReturnIcon />} label="Page Total" value={fmt(grandTotal)} accent={TEAL} sub={`${rows.length} of ${total} records`} />
          <StatCard icon={<CurrencyRupeeIcon />} label="Cash Refunds" value={fmt(cashTotal)} accent="#2e7d32" sub="this page" />
          <StatCard icon={<CardGiftcardIcon />} label="Credit Note Refunds" value={fmt(cnTotal)} accent="#4527a0" sub="this page" />
          <StatCard icon={<AccountBalanceIcon />} label="Online Refunds" value={fmt(onlineTotal)} accent="#1565c0" sub="this page" />
        </Box>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5, fontSize: '0.82rem' }}>{error}</Alert>}

        {/* ── Main Card ── */}
        <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #f0f2f5', overflow: 'hidden' }}>

          {/* Filter bar */}
          <Box sx={{ px: 2.5, py: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8', background: '#fafcff' }}>
            <TextField
              {...kb.searchProps}
              size="small"
              placeholder="Search patient name, mobile or bill no…  ( / to focus )"
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#b0bec5', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1, minWidth: 220,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, fontSize: '0.82rem', background: '#fff',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: TEAL },
                  '&.Mui-focused fieldset': { borderColor: TEAL },
                },
              }}
            />
            <MDDatePicker
              label="From"
              value={fromDate}
              onChange={val => handleDateFilter(val || '', toDate)}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, background: '#fff',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: TEAL },
                  '&.Mui-focused fieldset': { borderColor: TEAL },
                },
              }}
            />
            <MDDatePicker
              label="To"
              value={toDate}
              onChange={val => handleDateFilter(fromDate, val || '')}
              sx={{
                width: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3, background: '#fff',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: TEAL },
                  '&.Mui-focused fieldset': { borderColor: TEAL },
                },
              }}
            />
            {(fromDate !== defaultFrom || toDate !== defaultTo) && (
              <Chip
                label="Reset Dates"
                size="small"
                clickable
                onClick={() => handleDateFilter(defaultFrom, defaultTo)}
                sx={{ fontSize: '0.65rem', height: 26, color: '#ef5350', background: '#fff3f3', border: '1px solid #ffcdd2', borderRadius: '20px' }}
              />
            )}
          </Box>

          {/* Refund-mode filter chips */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #f0f4f8' }}>
            {REFUND_FILTERS.map(f => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                clickable
                onClick={() => handleModeFilter(f.key)}
                sx={{
                  fontSize: '0.7rem', fontWeight: 700, height: 26, borderRadius: '20px',
                  color: refundMethod === f.key ? '#fff' : '#78909c',
                  background: refundMethod === f.key ? TEAL : '#f5f7fa',
                  border: `1px solid ${refundMethod === f.key ? TEAL : '#e2e8f0'}`,
                  '&:hover': { background: refundMethod === f.key ? TEAL_DARK : TEAL_LIGHT },
                }}
              />
            ))}
            {rows.length > 0 && (
              <Box sx={{ ml: 'auto', px: 1.8, py: 0.6, borderRadius: 2.5, background: 'linear-gradient(135deg, #e8f5e9, #f1f8f2)', border: '1px solid #a5d6a7' }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#2e7d32', fontWeight: 800, fontFamily: 'monospace' }}>
                  Total: {fmt(grandTotal)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Table */}
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
              <CircularProgress sx={{ color: TEAL }} size={36} />
              <Typography sx={{ fontSize: '0.8rem', color: '#b0bec5' }}>Loading returns…</Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 950 }}>
                <TableBody>
                  {/* Header row inside tbody */}
                  <TableRow sx={{ background: `linear-gradient(90deg, ${TEAL} 0%, ${TEAL_DARK} 100%)` }}>
                    {COL_HEADERS.map(h => (
                      <TableCell key={h} sx={{
                        fontSize: '0.65rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)',
                        textTransform: 'uppercase', letterSpacing: 0.7,
                        py: 1.4, whiteSpace: 'nowrap', borderBottom: 'none',
                        '&:first-of-type': { pl: 2.5 },
                        '&:last-of-type': { pr: 2.5 },
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>

                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <AssignmentReturnIcon sx={{ fontSize: 52, color: '#e0e0e0', mb: 1.5 }} />
                          <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#bdbdbd' }}>No returns found</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#d0d0d0', mt: 0.5 }}>Try adjusting your search or date range</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((r, i) => {
                    const bill = r.original_bill || {};
                    return (
                      <TableRow key={r.id} {...kb.getRowProps(i)} sx={{
                        background: i % 2 === 0 ? '#fff' : '#fafcff',
                        borderLeft: r.status === 'CANCELLED' ? '3px solid #e53935' : '3px solid #43a047',
                        transition: 'background 0.15s',
                        cursor: 'pointer',
                        '&:hover': { background: TEAL_LIGHT, boxShadow: `inset 0 0 0 1px ${TEAL}22` },
                        ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}),
                      }}>
                        {/* Return No */}
                        <TableCell sx={{ py: 1.5, pl: 2.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 700, color: TEAL, whiteSpace: 'nowrap' }}>{r.return_no || '—'}</Typography>
                        </TableCell>

                        {/* Date */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.76rem', color: '#546e7a', whiteSpace: 'nowrap' }}>{fmtDate(r.return_date)}</Typography>
                        </TableCell>

                        {/* Patient */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#2d3748' }}>{bill.patient_name || '—'}</Typography>
                        </TableCell>

                        {/* Mobile */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#78909c' }}>{bill.patient_mobile || '—'}</Typography>
                        </TableCell>

                        {/* Bill No */}
                        <TableCell sx={{ py: 1.5 }}>
                          {bill.id ? (
                            <Button
                              size="small"
                              endIcon={<OpenInNewIcon sx={{ fontSize: '11px !important' }} />}
                              onClick={() => navigate(`/pharmacy/billing/${bill.id}`)}
                              sx={{
                                fontSize: '0.73rem', color: TEAL, p: 0, minWidth: 0,
                                fontFamily: 'monospace', fontWeight: 700,
                                '&:hover': { color: TEAL_DARK, background: 'transparent', textDecoration: 'underline' },
                              }}>
                              {bill.bill_no || '—'}
                            </Button>
                          ) : <Typography sx={{ fontSize: '0.75rem', color: '#ccc' }}>—</Typography>}
                        </TableCell>

                        {/* Reason */}
                        <TableCell sx={{ py: 1.5, maxWidth: 180 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: '#78909c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason || '—'}</Typography>
                        </TableCell>

                        {/* Refund Mode */}
                        <TableCell sx={{ py: 1.5 }}>
                          <ModeBadge mode={r.refund_method} />
                        </TableCell>

                        {/* Amount */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', fontFamily: 'monospace', fontWeight: 800, color: '#c62828', whiteSpace: 'nowrap' }}>{fmt(r.return_amount)}</Typography>
                        </TableCell>

                        {/* Status */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Chip label={r.status} size="small" sx={{
                            fontSize: '0.65rem', fontWeight: 700, height: 20,
                            color: r.status === 'CANCELLED' ? '#c62828' : '#2e7d32',
                            background: r.status === 'CANCELLED' ? '#fdecea' : '#e8f5e9',
                          }} />
                        </TableCell>

                        {/* Action */}
                        <TableCell sx={{ py: 1.5, pr: 2.5 }}>
                          <Tooltip title="Print Return Note" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handlePrint(r)}
                              sx={{
                                color: TEAL,
                                border: `1px solid ${TEAL}33`,
                                borderRadius: 1.5,
                                width: 30, height: 30,
                                '&:hover': { background: TEAL_LIGHT, borderColor: TEAL },
                              }}>
                              <PrintIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              px: 3, py: 1.75, borderTop: '1px solid #f0f4f8', background: '#fafcff',
            }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#90a4ae', fontWeight: 500 }}>
                Showing <b style={{ color: '#546e7a' }}>{(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)}</b> of <b style={{ color: '#546e7a' }}>{total}</b> results
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page <= 1 ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowLeftIcon fontSize="small" />
                </IconButton>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, idx) => {
                  const p = totalPages <= 5 ? idx + 1 : (page <= 3 ? idx + 1 : page - 2 + idx);
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button
                      key={p}
                      size="small"
                      onClick={() => setPage(p)}
                      sx={{
                        minWidth: 32, height: 32, borderRadius: 2, fontSize: '0.78rem', fontWeight: 700,
                        border: `1.5px solid ${p === page ? TEAL : '#e2e8f0'}`,
                        background: p === page ? `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})` : 'transparent',
                        color: p === page ? '#fff !important' : '#78909c',
                        boxShadow: p === page ? `0 2px 8px ${TEAL}44` : 'none',
                        '&:hover': p === page ? { color: '#fff !important' } : { background: TEAL_LIGHT, borderColor: TEAL, color: TEAL },
                      }}>
                      {p}
                    </Button>
                  );
                })}
                <IconButton
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  sx={{
                    border: '1.5px solid #e2e8f0', borderRadius: 2, width: 32, height: 32,
                    color: page >= totalPages ? '#d0d0d0' : TEAL,
                    '&:hover:not(:disabled)': { background: TEAL_LIGHT, borderColor: TEAL },
                  }}>
                  <KeyboardArrowRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
